import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Building2, Edit2, Trash2, Calendar, Hash } from 'lucide-react';
import { useCurrency } from '../../hooks/useCurrency';
import type { FixedAsset } from '../../types/fixed-assets';
import {
  FIXED_ASSET_STATUS_LABELS,
  FIXED_ASSET_STATUS_COLORS,
  DEPRECIATION_METHOD_LABELS,
} from '../../types/fixed-assets';

interface FixedAssetDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: FixedAsset;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function FixedAssetDetailModal({ isOpen, onClose, asset, onEdit, onDelete }: FixedAssetDetailModalProps) {
  const { formatConverted } = useCurrency();

  const progress = asset.acquisitionCost > 0
    ? Math.round((asset.cumulativeDepreciation / asset.acquisitionCost) * 100)
    : 0;

  const colors = FIXED_ASSET_STATUS_COLORS[asset.status];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Détail de l'immobilisation" size="lg">
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{asset.name}</h3>
              {asset.reference && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Hash className="h-3 w-3" /> {asset.reference}
                </p>
              )}
              {asset.description && (
                <p className="text-sm text-muted-foreground mt-1">{asset.description}</p>
              )}
            </div>
          </div>
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}>
            {FIXED_ASSET_STATUS_LABELS[asset.status]}
          </span>
        </div>

        {/* Données financières */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-muted-foreground">Coût d'acquisition</p>
            <p className="text-lg font-semibold text-foreground">{formatConverted(asset.acquisitionCost)}</p>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-muted-foreground">Amort. cumulé</p>
            <p className="text-lg font-semibold text-foreground">{formatConverted(asset.cumulativeDepreciation)}</p>
          </div>
          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-xs text-muted-foreground">Valeur nette comptable</p>
            <p className="text-lg font-semibold text-primary">{formatConverted(asset.netBookValue)}</p>
          </div>
        </div>

        {/* Barre de progression */}
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Progression de l'amortissement</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>

        {/* Détails technique */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground">Méthode d'amortissement</p>
              <p className="font-medium text-foreground">{DEPRECIATION_METHOD_LABELS[asset.depreciationMethod]}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Durée de vie</p>
              <p className="font-medium text-foreground">{asset.usefulLifeYears} ans</p>
            </div>
            <div>
              <p className="text-muted-foreground">Valeur résiduelle</p>
              <p className="font-medium text-foreground">{formatConverted(asset.residualValue)}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" /> Date d'acquisition</p>
              <p className="font-medium text-foreground">{new Date(asset.acquisitionDate).toLocaleDateString('fr-FR')}</p>
            </div>
            {asset.disposalDate && (
              <div>
                <p className="text-muted-foreground">Date de cession</p>
                <p className="font-medium text-foreground">{new Date(asset.disposalDate).toLocaleDateString('fr-FR')}</p>
              </div>
            )}
            <div>
              <p className="text-muted-foreground">Compte d'immob.</p>
              <p className="font-mono text-xs">{asset.assetAccountCode || asset.assetAccountId}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Compte d'amort.</p>
              <p className="font-mono text-xs">{asset.depreciationAccountCode || asset.depreciationAccountId}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="secondary" onClick={onClose}>Fermer</Button>
          {onEdit && asset.status === 'active' && (
            <Button variant="outline" icon={Edit2} onClick={onEdit}>Modifier</Button>
          )}
          {onDelete && asset.status === 'active' && (
            <Button variant="danger" icon={Trash2} onClick={onDelete}>Supprimer</Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
